import { Loader } from '@/ui/feedback/Loader';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';

import IFrameRoutes from 'App/IFrameRoutes';
import PrivateRoutes from 'App/PrivateRoutes';
import PublicRoutes from 'App/PublicRoutes';
import Tracker from 'App/Tracker';
import {
  GLOBAL_DESTINATION_PATH,
  IFRAME,
  JWT_PARAM,
  SITE_ID_STORAGE_KEY,
  SPOT_ONBOARDING,
} from 'App/constants/storageKeys';
import Layout from 'App/layout/Layout';
import { prefetchPath } from 'App/layout/nav/prefetchRoutes';
import { useStore } from 'App/mstore';
import { useLocation, useNavigate } from 'App/routing';
import { checkParam, handleSpotJWT, isTokenExpired } from 'App/utils';
import { ModalProvider } from 'Components/Modal';
import { ModalProvider as NewModalProvider } from 'Components/ModalContext';

import * as routes from './routes';

const Router: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mstore = useStore();
  const { projectsStore, sessionStore, searchStore, userStore, settingsStore } =
    mstore;
  const { jwt } = userStore;
  const { changePassword } = userStore.account;
  const userInfoLoading = userStore.fetchInfoRequest.loading;
  const localSpotJwt = userStore.spotJwt;
  const isLoggedIn = Boolean(jwt && !changePassword);
  const { fetchUserInfo } = userStore;
  const setJwt = userStore.updateJwt;
  const { logout } = userStore;

  const { setSessionPath } = sessionStore;
  const { siteId } = projectsStore;
  const { sitesLoading } = projectsStore;
  const sites = projectsStore.list;
  const loading = Boolean(userInfoLoading || sitesLoading);
  const initSite = projectsStore.initProject;
  const fetchSiteList = projectsStore.fetchList;

  const params = new URLSearchParams(location.search);
  const spotCb = params.get('spotCallback');
  const spotReqSent = React.useRef(false);
  const [isSpotCb, setIsSpotCb] = React.useState(false);
  const [isSignup, setIsSignup] = React.useState(false);
  const [isIframe, setIsIframe] = React.useState(false);
  const [isJwt, setIsJwt] = React.useState(false);

  const handleJwtFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const urlJWT = params.get('jwt');
    const spotJwt = params.get('spotJwt');
    if (spotJwt) {
      handleSpotLogin(spotJwt);
    }
    if (urlJWT) {
      setJwt({ jwt: urlJWT, spotJwt: spotJwt ?? undefined });
    }
  };

  const handleSpotLogin = (jwt: string) => {
    if (spotReqSent.current) {
      return;
    }
    spotReqSent.current = true;
    setIsSpotCb(false);

    handleSpotJWT(jwt);
  };

  const handleDestinationPath = () => {
    if (!isLoggedIn && location.pathname !== routes.login()) {
      localStorage.setItem(
        GLOBAL_DESTINATION_PATH,
        location.pathname + location.search,
      );
    }
  };

  const handleUserLogin = async () => {
    if (isSpotCb) {
      localStorage.setItem(SPOT_ONBOARDING, 'true');
    }
    // tenantId is only needed to pick a site once the list has arrived, so
    // both requests can be in flight at once.
    const sitesPending = projectsStore.prefetchList();
    const userData = await fetchUserInfo();
    const siteIdFromPath = location.pathname.split('/')[1];
    await fetchSiteList(siteIdFromPath, userData?.tenantId, sitesPending);

    if (userData?.tenantId) {
      projectsStore.setTenantId(userData.tenantId);
      // fetchSiteList already chose the path's project, else the stored one,
      // else the first: remember that one rather than going back to the stored
      // one, which sent a link into another project to the last-used project
      if (projectsStore.siteId) {
        localStorage.setItem(
          SITE_ID_STORAGE_KEY,
          `${projectsStore.siteId}_$_${userData.tenantId}`,
        );
      }
    }

    if (localSpotJwt && !isTokenExpired(localSpotJwt)) {
      handleSpotLogin(localSpotJwt);
    }

    const destinationPath = localStorage.getItem(GLOBAL_DESTINATION_PATH);
    if (
      destinationPath &&
      !destinationPath.includes(routes.login()) &&
      !destinationPath.includes(routes.signup()) &&
      destinationPath !== '/'
    ) {
      const url = new URL(destinationPath, window.location.origin);
      checkParams(url.search);
      navigate(destinationPath);
      localStorage.removeItem(GLOBAL_DESTINATION_PATH);
    }
  };

  const checkParams = (search?: string) => {
    const _isIframe = checkParam('iframe', IFRAME, search);
    const _isJwt = checkParam('jwt', JWT_PARAM, search);
    setIsIframe(_isIframe);
    setIsJwt(_isJwt);
  };

  useEffect(() => {
    checkParams();
    handleJwtFromUrl();
    // the page's own chunk downloads while /account and /projects load
    if (isLoggedIn) prefetchPath(location.pathname);
    mstore.initClient();

    const vmodeParam = new URLSearchParams(location.search).get('vmode');
    if (vmodeParam) {
      settingsStore.sessionSettings.updateKey('virtualMode', true);
    }
  }, []);

  useEffect(() => {
    if (spotCb) {
      setIsSpotCb(true);
    }
    if (location.pathname.includes('signup')) {
      setIsSignup(true);
    }
  }, [spotCb]);

  useEffect(() => {
    handleDestinationPath();

    setSessionPath(previousLocation || location);
  }, [location]);

  useEffect(() => {
    if (prevIsLoggedIn !== isLoggedIn && isLoggedIn) {
      void handleUserLogin();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && isSpotCb && !isSignup) {
      if (localSpotJwt && !isTokenExpired(localSpotJwt)) {
        handleSpotLogin(localSpotJwt);
      } else {
        void logout();
      }
    }
  }, [isSpotCb, isLoggedIn, localSpotJwt, isSignup]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchData = async () => {
      if (siteId && siteId !== lastFetchedSiteIdRef.current) {
        const activeSite = sites.find((s) => s.id == siteId);
        initSite(activeSite ?? {});
        lastFetchedSiteIdRef.current = activeSite?.id;
        await searchStore.fetchSavedSearchList();
      }
    };

    void fetchData();
  }, [siteId, isLoggedIn]);

  const lastFetchedSiteIdRef = useRef<any>(null);

  function usePrevious(value: any) {
    const ref = useRef<any>(undefined);
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }

  const prevIsLoggedIn = usePrevious(isLoggedIn);
  const previousLocation = usePrevious(location);

  const { pathname } = location;
  const immersive =
    pathname.includes('/session/') ||
    pathname.includes('/assist/') ||
    pathname.includes('multiview') ||
    pathname.includes('/view-spot/') ||
    pathname.includes('/spots/');
  // Preferences has its own rail: the menu folds while it is open
  const needsRoom = pathname.startsWith('/client/');
  const bare =
    pathname.includes('/mcp/authorize') || pathname.includes('/onboarding/');
  if (isIframe) {
    return (
      <IFrameRoutes isJwt={isJwt} isLoggedIn={isLoggedIn} loading={loading} />
    );
  }

  return isLoggedIn ? (
    <NewModalProvider>
      <ModalProvider>
        <Loader loading={loading} className="flex-1">
          <Tracker />
          <Layout immersive={immersive} needsRoom={needsRoom} bare={bare}>
            <PrivateRoutes />
          </Layout>
        </Loader>
      </ModalProvider>
    </NewModalProvider>
  ) : (
    <PublicRoutes />
  );
};

export default observer(Router);
